const RECENTLY_VIEWED_KEY = 'recently-viewed-products';
const MAX_PRODUCTS = 4;

/**
 * Get recently viewed products from localStorage
 * @returns {Array} Array of product objects
 */
function getRecentlyViewedProducts() {
  try {
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Save product to recently viewed list
 * If product already exists, keeps original position (does not move to front)
 * @param {Object} product - Product data to save
 */
function trackProductView(product) {
  if (!product || !product.id) return;

  const products = getRecentlyViewedProducts();

  // Check if product already exists - if so, don't update position
  const existingIndex = products.findIndex((p) => p.id === product.id);
  if (existingIndex !== -1) return;

  // Add new product to the beginning
  products.unshift({
    id: product.id,
    handle: product.handle,
    title: product.title,
    url: product.url,
    image: product.image,
    description: product.description,
    viewedAt: Date.now(),
  });

  // Keep only the most recent products
  const trimmed = products.slice(0, MAX_PRODUCTS);

  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(trimmed));
  } catch (e) {
    // localStorage might be full or unavailable
  }
}

/**
 * Web component for displaying recently viewed products
 */
class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.products = getRecentlyViewedProducts();
  }

  connectedCallback() {
    if (this.products.length === 0) {
      // Hide the entire section if no products
      this.hideSection();
      return;
    }

    this.render();
  }

  hideSection() {
    // Remove top padding from the next section (sermon calendars)
    const nextSection = this.nextElementSibling;
    if (nextSection) {
      nextSection.style.paddingTop = '0';
    }
    // Remove this section
    this.remove();
  }

  async render() {
    const grid = this.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    // Fetch fresh product data from Shopify's JSON API
    const productPromises = this.products.map((p) =>
      fetch(`/products/${p.handle}.json`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
    );

    const results = await Promise.all(productPromises);
    const validProducts = results.filter((r) => r && r.product).map((r) => r.product);

    if (validProducts.length === 0) {
      // No valid products found, hide section
      this.hideSection();
      return;
    }

    // Show the section and render cards
    this.style.display = '';
    grid.innerHTML = validProducts
      .map((product) => {
        const image = product.image ? product.image.src : (product.images?.[0]?.src || '');
        const description = this.stripHtml(product.body_html || '').substring(0, 150);
        const isBibleGuide = product.product_type === 'Bible Guide';

        return `
        <div>
          <a href="/products/${product.handle}" class="card-series ${isBibleGuide ? 'card-series--bible-guide' : ''}">
            ${
              image
                ? `
              <div class="card-series__image-wrapper">
                <img
                  src="${this.getResizedImageUrl(image, 533)}"
                  srcset="${this.getResizedImageUrl(image, 165)} 165w, ${this.getResizedImageUrl(image, 360)} 360w, ${this.getResizedImageUrl(image, 533)} 533w, ${this.getResizedImageUrl(image, 720)} 720w"
                  sizes="(min-width: 1200px) 280px, (min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw"
                  alt="${this.escapeHtml(product.title)}"
                  class="card-series__image"
                  loading="lazy"
                >
              </div>
            `
                : ''
            }
            <div class="card-series__content">
              <h3 class="card-series__title">${this.escapeHtml(product.title)}</h3>
              ${description ? `<p class="card-series__description">${this.escapeHtml(description)}</p>` : ''}
            </div>
          </a>
        </div>
      `;
      })
      .join('');
  }

  getResizedImageUrl(url, width) {
    // Shopify image URL transformation
    if (!url) return '';
    return url.replace(/(\.[^.]+)$/, `_${width}x$1`);
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

// Export for use in product pages
window.trackProductView = trackProductView;
